import React, { useEffect, useState, useCallback } from 'react';
import { finDeSession } from '../services/authInterceptor';

const BASE = 'https://besoins-tat-production.up.railway.app';

/**
 * PRÉVENIR AVANT, PAS APRÈS.
 *
 * Vécu 22.09 : « le token expire sans qu'on le sache ». Le contrat de
 * déconnexion fonctionnait — un 401, l'écran de connexion, le motif affiché —
 * mais il ne se déclenchait qu'au PROCHAIN appel. On l'apprenait donc au pire
 * moment : après vingt minutes passées à écrire un compte rendu, en cliquant
 * sur Enregistrer.
 *
 * Depuis la session glissante, travailler suffit à rester connecté : ce
 * bandeau ne se montre donc qu'à quelqu'un qui n'a RIEN fait depuis presque
 * une journée. C'est exactement la personne qui, sans lui, découvrirait la
 * chose en perdant sa saisie.
 *
 * TROIS RÈGLES :
 *
 * 1. IL NE PARLE QUE QUAND IL A QUELQUE CHOSE À DIRE. En dessous du seuil, il
 *    n'existe pas — un compte à rebours permanent fait le bruit qu'on apprend
 *    à ignorer, et c'est ce qu'on voulait éviter.
 * 2. IL PROPOSE LE GESTE, pas le constat. Un bandeau qui annonce une échéance
 *    sans bouton laisse chercher quoi faire. « Rester connecté » fait un appel
 *    au serveur — et c'est l'appel lui-même qui renouvelle la session, comme
 *    n'importe quelle autre action.
 * 3. IL NE MENT JAMAIS SUR CE QU'IL SAIT. La date vient du jeton ; s'il est
 *    illisible, le bandeau reste muet plutôt que d'inventer une échéance.
 */
const SEUIL_MS = 5 * 60 * 1000;   // on prévient dans les cinq dernières minutes
const PAS_MS = 15 * 1000;         // et on réévalue au quart de minute

export default function SessionQuiSeFerme({ actif = true }) {
  const [restant, setRestant] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!actif) { setRestant(null); return undefined; }
    const mesurer = () => {
      const fin = finDeSession();
      setRestant(fin ? fin - Date.now() : null);
    };
    mesurer();
    const t = setInterval(mesurer, PAS_MS);
    // Un onglet en arrière-plan ne reçoit pas ses minuteries au rythme prévu :
    // on remesure au retour, sinon le bandeau annonce une échéance dépassée.
    const auRetour = () => { if (!document.hidden) mesurer(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', auRetour); };
  }, [actif]);

  const rester = useCallback(async () => {
    setBusy(true);
    try {
      // N'IMPORTE QUEL APPEL RENOUVELLE — celui-ci est simplement le moins
      // cher. Le serveur repose le jeton en en-tête, l'intercepteur le range,
      // et la mesure suivante voit une échéance repoussée.
      const jeton = localStorage.getItem('token');
      if (!jeton) return;   // session déjà fermée : rien à prolonger
      await fetch(`${BASE}/auth/me`, { headers: { Authorization: `Bearer ${jeton}` } });
      const fin = finDeSession();
      setRestant(fin ? fin - Date.now() : null);
    } catch (e) { /* hors ligne : le bandeau reste, il redemandera */ }
    finally { setBusy(false); }
  }, []);

  if (!actif || restant == null || restant > SEUIL_MS) return null;

  const minutes = Math.max(0, Math.ceil(restant / 60000));
  const fini = restant <= 0;

  return (
    <div role="status" aria-live="polite" style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: '22px', zIndex: 3000,
      display: 'flex', alignItems: 'center', gap: '12px', maxWidth: 'calc(100vw - 32px)',
      padding: '10px 12px 10px 15px', borderRadius: '12px',
      background: 'var(--panel)', border: '1px solid var(--brand)',
      boxShadow: '0 12px 40px var(--hover-shadow)',
      fontSize: '12.5px', color: 'var(--text)', lineHeight: 1.45,
    }}>
      <i className="bi bi-hourglass-split" style={{ flex: 'none', color: 'var(--brand)' }} />
      <span style={{ minWidth: 0 }}>
        {fini
          ? <>Votre session est arrivée à échéance. <strong>Enregistrez ce que vous avez en cours</strong> avant de continuer.</>
          : <>Votre session se ferme dans <strong>{minutes} minute{minutes > 1 ? 's' : ''}</strong> faute d'activité.</>}
      </span>
      <button type="button" onClick={rester} disabled={busy}
        className="erp-btn erp-btn--primary erp-btn--sm"
        style={{ flex: 'none', opacity: busy ? 0.6 : 1 }}>
        {busy ? <i className="bi bi-arrow-repeat" style={{ animation: 'ats-spin .8s linear infinite' }} /> : null}
        {busy ? ' Un instant…' : 'Rester connecté'}
      </button>
    </div>
  );
}
